import { useRouter } from 'next/router';
import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { sessionStorageService } from '../service/storage';
import { SourcingBreadcrumb } from '@/shared/types/sourcing-marketplace';

interface HistoryListContextProps {
  historyList: string[];
  sourcingBreadcrumbs: SourcingBreadcrumb[];
  setHistoryList(data: string[]): void;
  getPreviousValidPath({ ignorePrefixes }: { ignorePrefixes: string[] }): string;
  setSourcingBreadcrumbs(newSourcingBreadcrumb: SourcingBreadcrumb[]): void;
}

const HistoryListContext = createContext<HistoryListContextProps>({} as HistoryListContextProps);

interface IProps {
  children: ReactNode;
}

export const HistoryListProvider = ({ children }: IProps) => {
  const { asPath } = useRouter();
  const { getStorage, setStorage } = sessionStorageService('HISTORY_LIST');
  const { getStorage: getSourcingBreadcrumbsStorage, setStorage: setSourcingBreadcrumbsStorage } =
    sessionStorageService('SOURCING_BREADCRUMBS');
  const [historyList, setHistoryList] = useState<string[]>((getStorage() as string[]) || []);
  const [sourcingBreadcrumbs, setSourcingBreadcrumbs] = useState<SourcingBreadcrumb[]>(
    (getSourcingBreadcrumbsStorage() as SourcingBreadcrumb[]) || [],
  );

  useEffect(() => {
    setSourcingBreadcrumbsStorage(sourcingBreadcrumbs);
  }, [sourcingBreadcrumbs]);

  useEffect(() => {
    setHistoryList((previousHistory) => {
      if (previousHistory[previousHistory.length - 1] === asPath) {
        return previousHistory;
      }

      const newHistory = [...previousHistory, asPath];

      if (newHistory.length > 10) {
        newHistory.shift();
      }

      return newHistory;
    });
  }, [asPath]);

  const getPreviousValidPath = ({ ignorePrefixes = [] }): string => {
    for (let i = historyList.length - 1; i >= 0; i--) {
      const currentPath = historyList[i];
      if (!ignorePrefixes?.some((ignorePrefix) => currentPath.indexOf(ignorePrefix) === 0)) {
        return currentPath;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!historyList.length) return;
    setStorage(historyList);
  }, [historyList]);

  return (
    <HistoryListContext.Provider
      value={{
        historyList,
        setHistoryList,
        getPreviousValidPath,
        sourcingBreadcrumbs,
        setSourcingBreadcrumbs,
      }}
    >
      {children}
    </HistoryListContext.Provider>
  );
};

export function useHistoryList(): HistoryListContextProps {
  const context = useContext(HistoryListContext);
  return context;
}
